import { ApolloClient, InMemoryCache } from '@apollo/client/core'
import { persistCache, LocalForageWrapper } from 'apollo3-cache-persist'
import localforage from 'localforage'
import { apolloLink } from './http-link'
import { reportError } from '@utils/report-error'

// A LOTO lockout step has NO IDENTITY OF ITS OWN.
//
// A checklist's steps are generated on demand and never stored, so the server
// has no id to give them and sends 0 for every one. Apollo's default
// normalization keys objects by `__typename:id`, which collapses all of them
// into the single entry "LotoLockoutStep:0": the query reads back as the FIRST
// step repeated once per step, and the screen draws nineteen identical rows.
//
// keyFields: false stores them INLINE in whatever returned them, which is also
// what they are. A step is only ever read as part of its checklist or its signed
// record, never fetched on its own, so there is nothing for normalization to
// buy here even once the ids are real. See lockout-cache.unit.js.
export const cacheConfig = {
  typePolicies: {
    LotoLockoutStep: { keyFields: false },
  },
}

const cache = new InMemoryCache(cacheConfig)

// Durable offline read cache. The previous backing store was localStorage,
// which is synchronous and capped at ~5MB. Too small for the app's cached
// query results (locations, devices, infrared/arc-flash data). IndexedDB via
// localforage gives us far more room and doesn't block the main thread.
const cacheStore = localforage.createInstance({
  name: 'bowvue',
  storeName: 'apollo_cache',
  description: 'Persisted Apollo InMemoryCache for offline reads',
})

// Kick off cache restoration immediately and expose the promise so the app can
// await hydration before mounting (see main.js). This prevents queries from
// firing against an empty cache on first paint and makes previously-viewed data
// available immediately when offline. A failure here must never block startup.
// we report it and continue with a cold (empty) cache.
export const cacheRestored = persistCache({
  cache,
  storage: new LocalForageWrapper(cacheStore),
}).catch((err) => {
  reportError(err, { source: 'apollo-cache-persist' })
})

export default new ApolloClient({
  link: apolloLink,
  cache,
  devtools: { enabled: process.env.NODE_ENV !== 'production' },
})
